import { Box, Divider, Grid, Paper, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from '../../../hooks/useTranslation';
import { tokens } from '../../../theme';
import { formatDateBR, formatMoney, isCPF } from '../../../utils';
import ScoreGauge from './ScoreGauge';
import { Button } from '../../../components/Button';
import CustomTable from '../../../components/CustomTable';
import HistoryModal from '../../redCoins/HistoryModal';

const valueOrDefault = (value, defaultText = '—') => {
  if (value === null || value === undefined || value === '') return defaultText;
  return value;
};

const InfoRow = ({ label, value, highlight }) => (
  <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75, flexWrap: 'wrap', alignItems: 'baseline' }}>
    <Typography fontSize={13} color="#4b5563" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
      {label}:
    </Typography>
    <Typography
      fontSize={13}
      fontWeight={600}
      color={highlight ?? '#1f2937'}
      sx={{ wordBreak: 'break-word', minWidth: 0 }}
    >
      {value}
    </Typography>
  </Box>
);

const SectionCard = ({ title, children, centerContent = false }) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: 2,
      p: 2,
      borderColor: '#e5e7eb',
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {title && (
      <Typography fontWeight={700} fontSize={14} mb={1.5} color="#1f2937">
        {title}
      </Typography>
    )}
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: centerContent ? 'center' : 'flex-start',
        alignItems: centerContent ? 'center' : 'stretch',
      }}
    >
      {children}
    </Box>
  </Paper>
);

const RedscoreHistoryTab = ({ orders = [] }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [selectedOrder, setSelectedOrder] = useState(null);

  if (orders.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
        <Typography color="text.secondary" fontSize={14}>
          Nenhuma consulta realizada para este cliente.
        </Typography>
      </Box>
    );
  }

  const latest = orders[0];
  const consult = latest?.consult;
  const isPerson = isCPF(latest?.customerData?.taxvar);

  const d = consult ?? {};

  const score = d.score ?? parseFloat(latest.score) ?? 0;
  const eventProbability = d.event_probability ?? 0;

  const protestOccurrences = d.protest?.occurrences ?? 0;
  const judicialActions = d.judicialAction?.occurrences ?? 0;
  const financialRestrictions = d.financialRestrictions?.occurrences ?? 0;
  const bankruptcies = d.bankruptcies?.occurrences ?? 0;
  const financialPendencies = d.financialPendencies?.occurrences ?? 0;
  const blockedByDataOwner = d.blockedByDataOwner ?? false;

  const taxIdStatus = d.tax_id_status;
  const taxRegime = d.tax_regime;
  const riskScreening = d.risk_screening;
  const birthDate = d.birthDate ?? d.birth_date;
  const motherName = d.motherName ?? d.mother_name;
  const tradeName = d.tradeName ?? d.trade_name;
  const openingDate = d.openingDate ?? d.opening_date;
  const companySize = d.companySize ?? d.company_size;
  const capital = d.capital;
  const operabilityIndicator = d.operabilityIndicator ?? d.operability_indicator;
  const name = d.name;

  const columns = [
    {
      field: 'score',
      headerName: t('consultation.agroscore').toUpperCase(),
      render: (value) => (
        <span
          style={{
            fontWeight: 700,
            fontSize: 14,
            color: colors.grey[200],
          }}
        >
          {parseFloat(value).toFixed(0)}
        </span>
      ),
    },
    {
      field: 'company',
      headerName: t('consultation.consultResponsible'),
      render: (value) => value?.name,
    },
    {
      field: 'created_at',
      headerName: t('consultation.consultDate'),
      render: (value) => formatDateBR(value),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
        <Box>
          <Typography fontWeight={700} fontSize={20} color="#1f2937">
            {t('customers.historyRedscore')}
          </Typography>
          <Typography fontSize={12} color="#6b7280" mt={0.25}>
            {t('customers.lastConsult')}: {formatDateBR(latest.created_at)}
          </Typography>
        </Box>
        <Button onClick={() => console.log()} fullWidth={false}>
          {t('buttons.newConsult')}
        </Button>
      </Box>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <SectionCard title={t('consultation.infoCredits')} centerContent>
            <ScoreGauge
              score={score}
              riskPercentage={eventProbability * 100}
              riskLabel={
                isPerson ? t('consultation.percentageRisk') : t('consultation.defaultProbability')
              }
            />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <SectionCard>
            <Typography fontWeight={700} fontSize={14} mb={1.5} color="#1f2937">
              {t('consultation.financialData')}
            </Typography>
            <Grid container rowSpacing={1}>
              <Grid item xs={6} sm={6} md={4}>
                <InfoRow
                  label={t('consultation.protests')}
                  value={valueOrDefault(protestOccurrences, '0')}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoRow
                  label={t('consultation.judicialActions')}
                  value={valueOrDefault(judicialActions, '0')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow
                  label={t('consultation.financialRestrictions')}
                  value={valueOrDefault(financialRestrictions, '0')}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoRow
                  label={t('consultation.bankruptcies')}
                  value={valueOrDefault(bankruptcies, '0')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow
                  label={t('consultation.financialPendencies')}
                  value={financialPendencies ? formatMoney(financialPendencies) : 'R$ 0,00'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow
                  label={t('consultation.blockedByOwner')}
                  value={blockedByDataOwner ? t('consultation.yes') : t('consultation.no')}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography fontWeight={700} fontSize={14} mb={1.5} color="#1f2937">
              {t('consultation.basicInfo')}
            </Typography>
            <Grid container rowSpacing={1}>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow
                  label={t('consultation.cadastralSituation')}
                  value={valueOrDefault(taxIdStatus)}
                  highlight={taxIdStatus === 'ATIVA' ? colors.greenAccent[500] : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow label={t('consultation.taxRegime')} value={valueOrDefault(taxRegime)} />
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoRow
                  label={t('consultation.riskClassification')}
                  value={valueOrDefault(riskScreening)}
                  highlight={riskScreening === 'BAIXO' ? colors.greenAccent[500] : undefined}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoRow
                  label={t('consultation.defaultRisk')}
                  value={`${(eventProbability * 100).toFixed(2)}%`}
                  highlight={colors.greenAccent[500]}
                />
              </Grid>

              {isPerson ? (
                <>
                  <Grid item xs={6} sm={6} md={4}>
                    <InfoRow
                      label={t('consultation.birthDate')}
                      value={valueOrDefault(birthDate)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoRow
                      label={t('consultation.motherName')}
                      value={valueOrDefault(motherName)}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={8} md={4}>
                    <InfoRow
                      label={t('consultation.tradeName')}
                      value={valueOrDefault(tradeName ?? name)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={4}>
                    <InfoRow
                      label={t('consultation.companySize')}
                      value={valueOrDefault(companySize)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={4}>
                    <InfoRow
                      label={t('consultation.openingDate')}
                      value={valueOrDefault(openingDate)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={4}>
                    <InfoRow
                      label={t('consultation.socialCapital')}
                      value={capital ? formatMoney(capital) : '—'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={4}>
                    <InfoRow
                      label={t('consultation.operabilityIndicator')}
                      value={valueOrDefault(operabilityIndicator)}
                      highlight={
                        operabilityIndicator === 'Operável' ? colors.greenAccent[500] : undefined
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </SectionCard>
        </Grid>
      </Grid>

      <Box mt={3}>
        <Typography fontWeight={700} fontSize={15} color="#1f2937" mb={1.5}>
          {t('consultation.historyRedscore')}
        </Typography>
        <CustomTable
          columns={columns}
          rows={orders}
          rowKey="id"
          actions={[
            {
              icon: <VisibilityIcon sx={{ fontSize: 18 }} />,
              tooltip: t('buttons.view'),
              onClick: (row) => setSelectedOrder(row),
            },
          ]}
          emptyMessage={t('messages.noConsultsFound')}
        />
      </Box>

      {/* Modal de detalhes da consulta */}
      <HistoryModal
        open={!!selectedOrder}
        handleClose={() => setSelectedOrder(null)}
        row={selectedOrder}
      />
    </Box>
  );
};

export default RedscoreHistoryTab;
